import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { adminAPI } from '../../services/api';
import {
  Users,
  Building2,
  Calendar,
  CreditCard,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  ArrowRight,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2
} from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, affiliatesData] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getAffiliates({ limit: 5 })
      ]);
      setStats(statsData);
      setAffiliates(affiliatesData);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message);
      // Use fallback data if API fails
      setStats({
        total_affiliates: 0,
        active_affiliates: 0,
        expiring_this_month: 0,
        new_this_month: 0,
        total_providers: 0,
        verified_providers: 0,
        total_revenue: 0,
        revenue_this_month: 0,
        pending_payments: 0,
        total_events: 0,
        upcoming_events: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
        </div>
      </AdminLayout>
    );
  }

  const quickStats = stats ? [
    {
      title: 'Afiliados Activos',
      value: stats.active_affiliates.toLocaleString(),
      total: stats.total_affiliates.toLocaleString(),
      icon: Users,
      trend: '+12%',
      trendUp: true,
      color: 'blue'
    },
    {
      title: 'Por Vencer Este Mes',
      value: stats.expiring_this_month,
      icon: AlertCircle,
      trend: 'Requiere atención',
      trendUp: false,
      color: 'amber'
    },
    {
      title: 'Nuevos Este Mes',
      value: stats.new_this_month,
      icon: TrendingUp,
      trend: '+8%',
      trendUp: true,
      color: 'green'
    },
    {
      title: 'Ingresos del Mes',
      value: `$${stats.revenue_this_month.toLocaleString()}`,
      icon: CreditCard,
      trend: '+15%',
      trendUp: true,
      color: 'purple'
    }
  ] : [];

  // Use data from API
  const recentAffiliates = affiliates.slice(0, 4);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Activo</Badge>;
      case 'expiring':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Por vencer</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Vencido</Badge>;
      default:
        return <Badge variant="outline">Desconocido</Badge>;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-dashboard-title">
              Dashboard
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Resumen general de UPIM
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <Button variant="outline" className="border-gray-300" asChild>
              <Link to="/admin/afiliados">
                Ver todos los afiliados
              </Link>
            </Button>
            <Button className="bg-gray-900 hover:bg-gray-800">
              Exportar reporte
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickStats.map((stat, index) => (
            <Card key={index} className="border border-gray-100">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                    {stat.total && (
                      <p className="text-xs text-gray-400 mt-1">de {stat.total} totales</p>
                    )}
                  </div>
                  <div className={`h-10 w-10 rounded-lg bg-${stat.color}-100 flex items-center justify-center`}>
                    <stat.icon className={`h-5 w-5 text-${stat.color}-600`} />
                  </div>
                </div>
                <div className="mt-3 flex items-center text-sm">
                  {stat.trendUp ? (
                    <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-500 mr-1" />
                  )}
                  <span className={stat.trendUp ? 'text-green-600' : 'text-amber-600'}>
                    {stat.trend}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Affiliates */}
          <Card className="lg:col-span-2 border border-gray-100">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">Afiliados Recientes</CardTitle>
              <Button variant="ghost" size="sm" className="text-gray-600" asChild>
                <Link to="/admin/afiliados">
                  Ver todos
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentAffiliates.map((affiliate) => (
                  <div 
                    key={affiliate.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`affiliate-row-${affiliate.id}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">
                          {affiliate.name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{affiliate.name}</p>
                        <p className="text-sm text-gray-500">{affiliate.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right hidden sm:block">
                        <p className="text-sm text-gray-600">{affiliate.plan || 'Sin plan'}</p>
                        <p className="text-xs text-gray-400">{affiliate.end_date ? new Date(affiliate.end_date).toLocaleDateString() : '-'}</p>
                      </div>
                      {getStatusBadge(affiliate.status)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions & Alerts */}
          <div className="space-y-6">
            {/* Pending Actions */}
            <Card className="border border-gray-100">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Acciones Pendientes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Membresías por vencer</p>
                      <p className="text-xs text-gray-500">{stats?.expiring_this_month || 0} este mes</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                    Ver
                  </Button>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-center space-x-3">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Pagos fallidos</p>
                      <p className="text-xs text-gray-500">{stats?.pending_payments || 0} pendientes</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
                    Ver
                  </Button>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Eventos próximos</p>
                      <p className="text-xs text-gray-500">{stats?.upcoming_events || 0} eventos</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                    Ver
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* System Status */}
            <Card className="border border-gray-100">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Estado del Sistema</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Pagos (Stripe)</span>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mr-1" />
                    <span className="text-sm text-green-600">Operativo</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Email (SendGrid)</span>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mr-1" />
                    <span className="text-sm text-green-600">Operativo</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">WhatsApp API</span>
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 text-amber-500 mr-1" />
                    <span className="text-sm text-amber-600">Pendiente</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
